// Sections: where a tune's feel changes, kept as a PLAYBACK choice rather than
// written into the score (user ask Sep 2026: "Latin for the verses and swing for
// the chorus… without putting instruction in the score, more like an additional
// playback option").
//
// A written Feel cue puts the WHERE in the score and the HOW (a band per feel)
// in the tune's playback settings. Sections move the WHERE out too: a list of
// section starts by written bar, each with a label (A, B, Intro…), and a band
// per label, all stored with the tune's playback settings. The playback side
// is the same as for written cues: every played bar is labelled with its
// section, and the feel-span machinery turns that into spans the band follows.
//
// Starts are WRITTEN BAR INDEXES, never measure ids: an iReal chart is
// re-parsed with fresh ids on every load (the voicing-pins lesson), and an
// index survives every source.
//
// Where a tune has no stored starts they are DETECTED, so nothing needs
// setting up: the chart's own section letters when it has them, else a guess
// from the changes — the opening strain is A, and where its first four bars
// come back is A again; what lies between is B (and C, when it differs from
// B). Voyage, which carries no letters at all, reads A 1–9, B 10–17, A 18–25.
//
// Pure: no store, no React.

import { analyzeForm } from './form';
import type { IRealChart, IRealMeasure } from './ireal/types';
import { pitchClassOf } from './notes';

export interface SectionStart {
  /** Written bar index in `chart.measures`; a pickup bar is index 0. */
  bar: number;
  label: string;
}

export interface DetectedSections {
  starts: SectionStart[];
  /** Read off the changes rather than the chart's own section letters. */
  guessed: boolean;
}

export const SECTION_LABEL_MAX = 20;
export const SECTION_STARTS_MAX = 64;
/** The labels the editor offers. Any label a chart writes is kept as written. */
export const SECTION_CHOICES = ['A', 'B', 'C', 'D', 'Intro', 'Verse', 'Coda'] as const;

/** A section label as a player reads it: iReal's `i` and `v` spelt out. */
export function sectionLabel(raw: string): string {
  const label = raw.trim();
  if (/^i(ntro)?$/i.test(label)) return 'Intro';
  if (/^v(erse)?$/i.test(label)) return 'Verse';
  return label.slice(0, SECTION_LABEL_MAX);
}

/** Distinct labels in first-appearance order. */
export function distinctLabels(starts: readonly SectionStart[]): string[] {
  const out: string[] = [];
  for (const start of starts) if (!out.includes(start.label)) out.push(start.label);
  return out;
}

/** The chart's own section letters as starts. */
export function writtenSections(measures: readonly IRealMeasure[]): SectionStart[] {
  const marks: SectionStart[] = [];
  measures.forEach((measure, bar) => {
    const label = measure.section ? sectionLabel(measure.section) : '';
    if (label) marks.push({ bar, label });
  });
  if (!marks.length) return marks;
  const pickup = measures[0]?.pickupBeats != null ? 1 : 0;
  // Bars before the first letter are an introduction; a letter on the first
  // full bar governs the pickup that leads into it.
  if (marks[0].bar > pickup) marks.unshift({ bar: 0, label: 'Intro' });
  else marks[0] = { ...marks[0], bar: 0 };
  return marks;
}

/** One bar's harmony as a comparable string; an empty bar holds the chord in force. */
function barSignatures(measures: readonly IRealMeasure[]): string[] {
  const out: string[] = [];
  let inForce = '';
  const pc = (note: string) => {
    try {
      return String(pitchClassOf(note));
    } catch {
      return note;
    }
  };
  measures.forEach((measure, index) => {
    if (measure.barRepeat && measure.chords.length === 0) {
      const back = out.slice(Math.max(0, index - measure.barRepeat), index).join('|');
      out.push(back);
      return;
    }
    if (measure.chords.length === 0) {
      out.push(inForce);
      return;
    }
    const chords = measure.chords.map((chord) =>
      chord.noChord
        ? 'nc'
        : `${pc(chord.root)}${chord.chordType ?? chord.quality}${chord.bass ? `/${pc(chord.bass)}` : ''}`,
    );
    inForce = chords[chords.length - 1];
    out.push(chords.join(','));
  });
  return out;
}

/**
 * The bar after a repeated opening strain, its last ending included, or null
 * when the tune does not open on a repeat. A last ending is taken to be as
 * long as the first (charts rarely say otherwise).
 */
function repeatedStrainEnd(measures: readonly IRealMeasure[], from: number): number | null {
  // A closing repeat with no opening one repeats from the top (flatten's rule),
  // so the strain may open on a plain barline.
  let close = -1;
  for (let i = from; i < measures.length; i += 1) {
    if (i > from && measures[i].open === 'repeat') return null;
    if (measures[i].close === 'repeat') {
      close = i;
      break;
    }
  }
  if (close < 0) return null;
  let firstEnding = -1;
  for (let i = close; i >= from; i -= 1) {
    if (measures[i].ending === 1) {
      firstEnding = i;
      break;
    }
  }
  if (firstEnding < 0) return close + 1;
  const endingLength = close - firstEnding + 1;
  let end = close + 1;
  while (end < measures.length && (measures[end].ending ?? 0) > 1) end += endingLength;
  return Math.min(end, measures.length);
}

const WINDOW = 4;
const MIN_SECTION = 8;

/**
 * Sections guessed from the changes: A where the opening's first four bars
 * sound, the stretches between them B, C… (a stretch that opens like an
 * earlier one takes its label). Empty when nothing returns.
 */
export function guessSections(chart: IRealChart): SectionStart[] {
  const measures = chart.measures;
  const count = measures.length;
  let intro: { start: number; end: number } | null = null;
  try {
    intro = analyzeForm(chart).intro;
  } catch {
    intro = null;
  }
  const pickup = measures[0]?.pickupBeats != null ? 1 : 0;
  const from = intro ? intro.end + 1 : pickup;
  if (from + MIN_SECTION + WINDOW > count) return [];
  const sig = barSignatures(measures);
  const opening = sig.slice(from, from + WINDOW);
  if (opening.every((bar) => !bar)) return [];
  // A repeat around the whole form says nothing about its sections.
  const repeated = repeatedStrainEnd(measures, from);
  const strainEnd = repeated != null && repeated + MIN_SECTION <= count ? repeated : null;
  // How long an A runs: the repeated strain when there is one, else the gap to
  // its first return, capped at the usual eight bars. The search starts past
  // the first A (a section is at least eight bars when nothing says otherwise)
  // and steps a whole A after each return, so an A whose two halves match is
  // not read as two sections.
  let length = strainEnd != null ? strainEnd - from : 0;
  const returns: number[] = [];
  for (let at = strainEnd ?? from + MIN_SECTION; at + WINDOW <= count; at += 1) {
    if (!opening.every((bar, k) => sig[at + k] === bar)) continue;
    if (!length) length = Math.min(at - from, MIN_SECTION);
    returns.push(at);
    at += length - 1;
  }
  if (!returns.length || length < 2) return [];

  const starts: SectionStart[] = intro ? [{ bar: 0, label: 'Intro' }] : [];
  const others: { label: string; opening: string }[] = [];
  const labelFor = (at: number, tail: boolean): string => {
    const opening = sig.slice(at, at + WINDOW).join(';');
    const known = others.find((other) => other.opening === opening);
    if (known) return known.label;
    if (tail && count - at < length) return 'Coda';
    const label = 'BCDEFG'[others.length] ?? 'B';
    others.push({ label, opening });
    return label;
  };
  const aStarts = [from, ...returns];
  aStarts.forEach((at, index) => {
    starts.push({ bar: index === 0 && !intro ? 0 : at, label: 'A' });
    const next = aStarts[index + 1] ?? count;
    const other = at + length;
    if (next - at > length && other < count) {
      starts.push({ bar: other, label: labelFor(other, index === aStarts.length - 1) });
    }
  });
  return starts;
}

/** The sections a tune reads as with nothing stored, or null when it has none. */
export function detectSections(chart: IRealChart): DetectedSections | null {
  const written = writtenSections(chart.measures);
  if (distinctLabels(written).length >= 2) return { starts: written, guessed: false };
  const guess = guessSections(chart);
  if (distinctLabels(guess).length >= 2) return { starts: guess, guessed: true };
  return null;
}

/** Stored starts, guarded: sorted, one per bar, sane labels. Undefined when none survive. */
export function normalizeSectionStarts(raw: unknown): SectionStart[] | undefined {
  if (!Array.isArray(raw)) return undefined;
  const byBar = new Map<number, string>();
  for (const entry of raw.slice(0, SECTION_STARTS_MAX * 2)) {
    if (!entry || typeof entry !== 'object') continue;
    const { bar, label } = entry as { bar?: unknown; label?: unknown };
    if (typeof bar !== 'number' || !Number.isInteger(bar) || bar < 0 || bar > 4000) continue;
    if (typeof label !== 'string') continue;
    const name = sectionLabel(label);
    if (!name || byBar.has(bar)) continue;
    byBar.set(bar, name);
  }
  const starts = [...byBar.entries()]
    .sort(([a], [b]) => a - b)
    .slice(0, SECTION_STARTS_MAX)
    .map(([bar, label]) => ({ bar, label }));
  return starts.length ? starts : undefined;
}

/** The section containing a written bar, or undefined before the first start. */
export function sectionAt(starts: readonly SectionStart[], bar: number): string | undefined {
  let label: string | undefined;
  for (const start of starts) {
    if (start.bar > bar) break;
    label = start.label;
  }
  return label;
}

export interface SectionRow {
  label: string;
  /** Inclusive written-bar ranges this label covers, in order. */
  ranges: [number, number][];
}

/** One row per label with the bars it covers (neighbouring ranges joined). */
export function sectionRows(starts: readonly SectionStart[], barCount: number): SectionRow[] {
  const rows: SectionRow[] = [];
  const inRange = starts.filter((start) => start.bar < barCount);
  inRange.forEach((start, index) => {
    const end = (inRange[index + 1]?.bar ?? barCount) - 1;
    if (end < start.bar) return;
    let row = rows.find((r) => r.label === start.label);
    if (!row) {
      row = { label: start.label, ranges: [] };
      rows.push(row);
    }
    const last = row.ranges[row.ranges.length - 1];
    if (last && last[1] + 1 === start.bar) last[1] = end;
    else row.ranges.push([start.bar, end]);
  });
  return rows;
}

/**
 * The played form labelled by section, ready for `feelSpans`: each played
 * bar carries the label of the section its written bar sits in, so a repeat,
 * a D.S. or a loop starting mid-section all play the right band. Consecutive
 * bars of one section coalesce into a single span there.
 */
export function sectionCueMeasures(
  playedWritten: readonly (number | undefined)[],
  starts: readonly SectionStart[],
): { feel?: { label: string } }[] {
  return playedWritten.map((bar) => {
    const label = bar === undefined ? undefined : sectionAt(starts, bar);
    return label ? { feel: { label } } : {};
  });
}

/** The printed bar number of a written bar (a pickup bar is not counted). */
export function printedBarNumber(measures: readonly IRealMeasure[], bar: number): number {
  const pickup = measures[0]?.pickupBeats != null ? 1 : 0;
  return Math.max(1, bar + 1 - pickup);
}

/** The written bar index of a printed bar number. */
export function writtenBarOf(measures: readonly IRealMeasure[], printed: number): number {
  const pickup = measures[0]?.pickupBeats != null ? 1 : 0;
  return Math.max(0, Math.min(measures.length - 1, Math.round(printed) - 1 + pickup));
}
